import React, { useState, useEffect } from 'react';
import { db, storage } from '@/lib/firebase';
import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '@/AuthContext';
import { ToastContext } from '@/components/ToastProvider.jsx';
import { Plus, Edit, Trash2, Eye, EyeOff, Calendar, Tag, Image as ImageIcon, BarChart3, X, Upload, Link, Trash } from 'lucide-react';

export default function PromotionManagement() {
  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    couponCode: '',
    discountType: 'percentage', // 'percentage' or 'fixed'
    discount: '',
    validFrom: '',
    validUntil: '',
    isActive: true,
    priority: 1,
    targetAudience: 'all',
    actionText: 'Get This Offer',
    maxUsagePerUser: 1, // New field for usage limits
    totalUsageLimit: null // New field for total usage limit (optional)
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'promotions'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const promoData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPromotions(promoData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      couponCode: '',
      discountType: 'percentage',
      discount: '',
      validFrom: '',
      validUntil: '',
      isActive: true,
      priority: 1,
      targetAudience: 'all',
      actionText: 'Get This Offer',
      maxUsagePerUser: 1,
      totalUsageLimit: null
    });
    setEditingPromo(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileName = `promotions/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      notify('Error uploading image. Please try again.', { type: 'error' });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setFormData({ ...formData, imageUrl: '' }); // Clear URL when file is selected
    }
  };

  const handleImageUrlChange = (e) => {
    setFormData({ ...formData, imageUrl: e.target.value });
    setImageFile(null);
    setImagePreview('');
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({ ...formData, imageUrl: '' });
  };

  // Auto-generate discount code
  const generateDiscountCode = () => {
    const adjectives = ['SAVE', 'DEAL', 'OFFER', 'SPECIAL', 'FLASH', 'SUMMER', 'WINTER', 'SPRING', 'FALL', 'HOLIDAY', 'WEEKEND', 'DAILY', 'MONTHLY', 'QUICK', 'FAST', 'BEST', 'TOP', 'PREMIUM', 'EXCLUSIVE', 'LIMITED'];
    const nouns = ['SALE', 'DISCOUNT', 'COUPON', 'VOUCHER', 'CODE', 'DEAL', 'OFFER', 'PRICE', 'RATE', 'VALUE', 'BONUS', 'GIFT', 'REWARD', 'BENEFIT', 'ADVANTAGE', 'OPPORTUNITY', 'CHANCE', 'LUCK', 'FORTUNE', 'SUCCESS'];
    const numbers = Math.floor(Math.random() * 900) + 100; // 100-999
    
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    
    const generatedCode = `${adjective}${noun}${numbers}`;
    setFormData({ ...formData, couponCode: generatedCode });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let finalImageUrl = formData.imageUrl;
      
      // Upload image if file is selected
      if (imageFile) {
        const uploadedUrl = await handleImageUpload(imageFile);
        if (uploadedUrl) {
          finalImageUrl = uploadedUrl;
        } else {
          return; // Stop if upload failed
        }
      }

      // Prepare base data
      const baseData = {
        ...formData,
        imageUrl: finalImageUrl,
        discount: formData.discount ? Number(formData.discount) : null,
        priority: Number(formData.priority),
        validFrom: new Date(formData.validFrom),
        validUntil: new Date(formData.validUntil),
        updatedAt: serverTimestamp(),
        createdBy: user.uid
      };

      // Add creation-specific fields only for new promotions
      if (!editingPromo) {
        baseData.createdAt = serverTimestamp();
        baseData.viewCount = 0;
        baseData.clickCount = 0;
        baseData.conversionCount = 0;
      }

      // Filter out undefined values for Firestore
      const promoData = Object.fromEntries(
        Object.entries(baseData).filter(([_, value]) => value !== undefined)
      );

      if (editingPromo) {
        await updateDoc(doc(db, 'promotions', editingPromo.id), promoData);
        notify('Promotion updated successfully!', { type: 'success' });
      } else {
        await addDoc(collection(db, 'promotions'), promoData);
        notify('Promotion created successfully!', { type: 'success' });
      }

      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving promotion:', error);
      notify('Error saving promotion. Please try again.', { type: 'error' });
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title || '',
      description: promo.description || '',
      imageUrl: promo.imageUrl || '',
      couponCode: promo.couponCode || '',
      discountType: promo.discountType || 'percentage',
      discount: promo.discount || '',
      validFrom: promo.validFrom?.toDate?.()?.toISOString().split('T')[0] || '',
      validUntil: promo.validUntil?.toDate?.()?.toISOString().split('T')[0] || '',
      isActive: promo.isActive ?? true,
      priority: promo.priority || 1,
      targetAudience: promo.targetAudience || 'all',
      actionText: promo.actionText || 'Get This Offer',
      maxUsagePerUser: promo.maxUsagePerUser || 1,
      totalUsageLimit: promo.totalUsageLimit || null
    });
    setImageFile(null);
    setImagePreview(promo.imageUrl || '');
    setShowForm(true);
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await deleteDoc(doc(db, 'promotions', promoId));
      notify('Promotion deleted successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error deleting promotion:', error);
      notify('Error deleting promotion. Please try again.', { type: 'error' });
    }
  };

  const toggleActive = async (promo) => {
    try {
      await updateDoc(doc(db, 'promotions', promo.id), {
        isActive: !promo.isActive,
        updatedAt: serverTimestamp()
      });
      notify(`Promotion ${promo.isActive ? 'deactivated' : 'activated'} successfully!`, { type: 'success' });
    } catch (error) {
      console.error('Error toggling promotion:', error);
      notify('Error updating promotion. Please try again.', { type: 'error' });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (promo) => {
    if (!promo.isActive) return 'bg-gray-100 text-gray-800';
    const now = new Date();
    const validFrom = promo.validFrom?.toDate?.() || new Date(promo.validFrom);
    const validUntil = promo.validUntil?.toDate?.() || new Date(promo.validUntil);
    
    if (now < validFrom) return 'bg-yellow-100 text-yellow-800';
    if (now > validUntil) return 'bg-red-100 text-red-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (promo) => {
    if (!promo.isActive) return 'Inactive';
    const now = new Date();
    const validFrom = promo.validFrom?.toDate?.() || new Date(promo.validFrom);
    const validUntil = promo.validUntil?.toDate?.() || new Date(promo.validUntil);
    
    if (now < validFrom) return 'Scheduled';
    if (now > validUntil) return 'Expired';
    return 'Active';
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Promotion Management</h2>
          <p className="text-gray-600 mt-1">Create and manage promotional content for the store splash screen</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Promotion
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {promotions.filter(p => p.isActive && getStatusText(p) === 'Active').length}
          </div>
          <div className="text-sm text-gray-600">Active Promotions</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {promotions.reduce((sum, p) => sum + (p.viewCount || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Total Views</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {promotions.reduce((sum, p) => sum + (p.clickCount || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Total Clicks</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">
            {promotions.length}
          </div>
          <div className="text-sm text-gray-600">Total Promotions</div>
        </div>
      </div>

      {/* Promotions List */}
      <div className="card p-0">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promotion
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Validity
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotions.map((promo) => (
                <tr key={promo.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      {promo.imageUrl && (
                        <img
                          src={promo.imageUrl}
                          alt={promo.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <div className="font-medium text-gray-900">{promo.title}</div>
                        <div className="text-sm text-gray-500">{promo.description}</div>
                                                 {promo.couponCode && (
                           <div className="text-xs font-mono text-blue-600 mt-1">
                             {promo.couponCode}
                           </div>
                         )}
                         {promo.discount && (
                           <div className="text-xs text-green-600 mt-1">
                             {promo.discountType === 'percentage' 
                               ? `${promo.discount}% off` 
                               : `₱${promo.discount} off`
                             }
                           </div>
                         )}
                         {promo.maxUsagePerUser && (
                           <div className="text-xs text-purple-600 mt-1">
                             Usage: {promo.maxUsagePerUser} per user
                             {promo.totalUsageLimit && ` • Total: ${promo.totalUsageLimit}`}
                           </div>
                         )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(promo)}`}>
                      {getStatusText(promo)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        From: {formatDate(promo.validFrom)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Until: {formatDate(promo.validUntil)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>Views: {promo.viewCount || 0}</div>
                      <div>Clicks: {promo.clickCount || 0}</div>
                      <div>CTR: {promo.viewCount ? ((promo.clickCount || 0) / promo.viewCount * 100).toFixed(1) : 0}%</div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(promo)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit promotion"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(promo)}
                        className={`p-2 rounded-lg transition-colors ${
                          promo.isActive 
                            ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50' 
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={promo.isActive ? 'Deactivate promotion' : 'Activate promotion'}
                      >
                        {promo.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(promo.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete promotion"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {promotions.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No promotions yet</p>
            <p className="text-sm">Create your first promotion to start engaging customers</p>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPromo ? 'Edit Promotion' : 'Create New Promotion'}
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="input w-full"
                      placeholder="Summer Sale!"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action Text
                    </label>
                    <input
                      type="text"
                      value={formData.actionText}
                      onChange={(e) => setFormData({ ...formData, actionText: e.target.value })}
                      className="input w-full"
                      placeholder="Get This Offer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input w-full"
                    placeholder="Get amazing discounts on our summer collection..."
                  />
                </div>

                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Promotion Image
                   </label>
                   
                   {/* Image Upload Tabs */}
                   <div className="flex space-x-1 mb-3">
                     <button
                       type="button"
                       onClick={() => {
                         setImageFile(null);
                         setImagePreview('');
                         setFormData({ ...formData, imageUrl: '' });
                       }}
                       className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                         !imageFile && !formData.imageUrl
                           ? 'bg-blue-100 text-blue-700'
                           : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                       }`}
                     >
                       <Upload className="w-4 h-4 inline mr-2" />
                       Upload Image
                     </button>
                     <button
                       type="button"
                       onClick={() => {
                         setImageFile(null);
                         setImagePreview('');
                       }}
                       className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                         formData.imageUrl && !imageFile
                           ? 'bg-blue-100 text-blue-700'
                           : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                       }`}
                     >
                       <Link className="w-4 h-4 inline mr-2" />
                       Image URL
                     </button>
                   </div>

                   {/* File Upload Input */}
                   {!formData.imageUrl && (
                     <div className="mb-3">
                       <input
                         type="file"
                         accept="image/*"
                         onChange={handleImageChange}
                         className="hidden"
                         id="image-upload"
                       />
                       <label
                         htmlFor="image-upload"
                         className="cursor-pointer block w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                       >
                         <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                         <span className="text-sm text-gray-600">
                           {uploading ? 'Uploading...' : 'Click to upload image or drag and drop'}
                         </span>
                         <p className="text-xs text-gray-500 mt-1">
                           PNG, JPG, GIF up to 5MB
                         </p>
                       </label>
                     </div>
                   )}

                   {/* URL Input */}
                   <div className="mb-3">
                     <input
                       type="url"
                       value={formData.imageUrl}
                       onChange={handleImageUrlChange}
                       className="input w-full"
                       placeholder="https://example.com/image.jpg"
                     />
                   </div>

                   {/* Image Preview */}
                   {(imagePreview || formData.imageUrl) && (
                     <div className="relative inline-block">
                       <img
                         src={imagePreview || formData.imageUrl}
                         alt="Preview"
                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                       />
                       <button
                         type="button"
                         onClick={removeImage}
                         className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                         title="Remove image"
                       >
                         <Trash className="w-4 h-4" />
                       </button>
                     </div>
                   )}
                 </div>

                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Coupon Code
                     </label>
                     <div className="flex gap-2">
                       <input
                         type="text"
                         value={formData.couponCode}
                         onChange={(e) => setFormData({ ...formData, couponCode: e.target.value })}
                         className="input flex-1"
                         placeholder="SUMMER20"
                       />
                       <button
                         type="button"
                         onClick={generateDiscountCode}
                         className="px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-sm font-medium transition-colors"
                         title="Generate a random discount code"
                       >
                         Generate
                       </button>
                     </div>
                     <p className="text-xs text-gray-500 mt-1">
                       Enter a custom code or click Generate for a random one
                     </p>
                   </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Type
                    </label>
                    <select
                      value={formData.discountType}
                      onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                      className="input w-full"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₱)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max={formData.discountType === 'percentage' ? "100" : ""}
                      step={formData.discountType === 'percentage' ? "1" : "0.01"}
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                      className="input w-full pr-12"
                      placeholder={formData.discountType === 'percentage' ? "20" : "100.00"}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500 text-sm">
                        {formData.discountType === 'percentage' ? '%' : '₱'}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.discountType === 'percentage' 
                      ? 'Enter percentage (0-100)' 
                      : 'Enter amount in pesos'
                    }
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid From *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.validFrom}
                      onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid Until *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.validUntil}
                      onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="input w-full"
                    >
                      <option value={1}>1 - Lowest</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>5 - Highest</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Audience
                    </label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                      className="input w-full"
                    >
                      <option value="all">All Users</option>
                      <option value="new_users">New Users Only</option>
                      <option value="returning">Returning Users Only</option>
                    </select>
                  </div>
                </div>

                {/* Usage Limits Section */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Usage Limits</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Usage Per User *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        required
                        value={formData.maxUsagePerUser}
                        onChange={(e) => setFormData({ ...formData, maxUsagePerUser: Number(e.target.value) })}
                        className="input w-full"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How many times can each user use this promotion? (e.g., 1 for one-time use, 5 for multiple uses)
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Usage Limit (Optional)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.totalUsageLimit || ''}
                        onChange={(e) => setFormData({ ...formData, totalUsageLimit: e.target.value ? Number(e.target.value) : null })}
                        className="input w-full"
                        placeholder="Unlimited"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum total uses across all users (leave empty for unlimited)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Active (visible to users)
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingPromo ? 'Update Promotion' : 'Create Promotion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
